import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useAuth } from '../../Context/auth'; // Ajuste o caminho

// Hook genérico de API
const useApiCall = () => {
    const { currentUser } = useAuth();
    return React.useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = { method, headers: { 'Authorization': `Bearer ${token}` }};
        if (data && method !== 'GET' && method !== 'HEAD') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(data);
        }
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição (${response.status})`);
        }
        if (response.status === 204) return null;
        return response.json();
    }, [currentUser]);
};

// Função de formatar data
const formatarData = (isoString) => {
    if (!isoString) return '—';
    const data = isoString.split('T')[0];
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
};

function ModalEstornarInsumo({ isVisible, onClose, insumo, onEstornoSucesso }) {
    const apiCall = useApiCall();
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEstorno, setLoadingEstorno] = useState(null); // ID da mov sendo estornada

    const fetchMovimentacoes = React.useCallback(async () => {
        if (!insumo?.id) return;
        setLoading(true);
        try {
            const data = await apiCall(`/api/producao/insumo/${insumo.id}/movimentacoes`);
            setMovimentacoes(data || []);
        } catch (error) {
            console.error("Erro ao buscar movimentações:", error);
            Swal.fire("Erro", "Não foi possível carregar as baixas anteriores.", "error");
        } finally {
            setLoading(false);
        }
    }, [apiCall, insumo?.id]);

    useEffect(() => {
        if (isVisible) {
            fetchMovimentacoes();
        } else {
            setMovimentacoes([]); // Limpa ao fechar
        }
    }, [isVisible, fetchMovimentacoes]);
    
     // Ajuste no handleEstornar para chamar o callback
    const handleEstornarClick = async (movId) => {
        setLoadingEstorno(movId);
        try {
            await apiCall('/api/producao/insumo/estornar', 'POST', {
                movimentacaoId: movId,
                producaoInsumoId: insumo.id
            });
            Swal.fire("Sucesso!", "Baixa estornada.", "success");
            fetchMovimentacoes(); // Recarrega a lista
            onEstornoSucesso(); // Chama o callback da tela principal
        } catch (error) {
            console.error("Erro ao estornar baixa:", error);
            Swal.fire("Erro!", error.message || "Não foi possível estornar a baixa.", "error");
        } finally {
            setLoadingEstorno(null);
        }
    };

    return (
        // Usando onHide={onEstornoSucesso} garante que a tela principal atualize ao fechar
        <Modal show={isVisible} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Estornar Baixa de Insumo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    <strong>Insumo:</strong> 
                    {/* === AJUSTE AQUI para incluir a cor === */}
                    {` ${insumo.insumo_codigo} - ${insumo.insumo_descricao}${insumo.cor ? ` -  ${insumo.cor}` : ''}`}
                </p>
                <p>Selecione a baixa (movimentação de saída) que deseja estornar:</p>
                
                {loading ? (
                    <div className="text-center p-3">
                        <Spinner animation="border" variant="primary" />
                        <p>Carregando baixas...</p>
                    </div>
                ) : (
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Descrição/Referência</th>
                                <th className="text-end">Quantidade</th>
                                <th className="text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movimentacoes.length > 0 ? (
                                movimentacoes.map(mov => (
                                    <tr key={mov.id}>
                                        <td>{formatarData(mov.data_movimentacao)}</td>
                                        <td>{mov.descricao}</td>
                                        <td className="text-end">{(parseFloat(mov.quantidade) || 0).toFixed(4)}</td>
                                        <td className="text-center">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleEstornarClick(mov.id)}
                                                disabled={loadingEstorno === mov.id}
                                            >
                                                {loadingEstorno === mov.id ? (
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                ) : (
                                                    <i className="fas fa-undo"></i>
                                                )}
                                                {' '} Estornar
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted">
                                        Nenhuma baixa encontrada para este insumo.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Fechar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalEstornarInsumo;